'use client'

import { memo, useCallback, useEffect, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { NINXL_DOWNLOAD_HREF } from './nin-org-links'

type NavLinkItem = { label: string; href: string; kind: 'link' }
type NavScrollItem = { label: string; id: string; kind: 'scroll' }
type NavItem = NavLinkItem | NavScrollItem

/** baby: Same destinations as classic /nin org nav: craft styling only. */
const NAV_ITEMS: NavItem[] = [
  { label: 'Projects', id: 'projects', kind: 'scroll' },
  { label: 'Connect', id: 'connect-platform', kind: 'scroll' },
  { label: 'Contact', id: 'contact', kind: 'scroll' },
  { label: 'NINX', href: '/ninxl', kind: 'link' },
  { label: 'NINK', href: '/nink', kind: 'link' },
  { label: 'Ningenie', href: '/home', kind: 'link' },
]

export const NinCraftNav = memo(function NinCraftNav({
  onScrollTo,
}: {
  onScrollTo?: (id: string) => void
}) {
  const pathname = usePathname() ?? ''
  const isOrgHome = pathname === '/nin' || pathname === '/nin/v1'
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const scrollTo = useCallback(
    (id: string) => {
      if (onScrollTo) {
        onScrollTo(id)
        return
      }
      const target =
        document.getElementById(id) ??
        document.querySelector<HTMLElement>(`[data-section="${id}"]`)
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    [onScrollTo],
  )

  return (
    <nav
      className={`craft-nav${scrolled ? ' is-scrolled' : ''}`}
      aria-label="NIN organization"
    >
      <div className="craft-nav__inner craft-nav__inner--wide">
        <Link href="/nin" className="craft-nav__brand">
          <Image
            src="/ninx_logo_light.svg"
            alt="NIN"
            width={28}
            height={28}
            className="craft-nav__mark"
            priority
          />
          <span className="craft-nav__wordmark">NIN</span>
        </Link>

        <div className="craft-nav__links craft-nav__links--dense">
          {NAV_ITEMS.map((item) => {
            if (item.kind === 'link') {
              const isActive =
                pathname === item.href || pathname.startsWith(`${item.href}/`)
              return (
                <Link
                  key={item.label}
                  href={item.href}
                  className={isActive ? 'is-active' : undefined}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {item.label}
                </Link>
              )
            }

            if (isOrgHome) {
              return (
                <button key={item.label} type="button" onClick={() => scrollTo(item.id)}>
                  {item.label}
                </button>
              )
            }

            return (
              <Link key={item.label} href={`/nin#${item.id}`}>
                {item.label}
              </Link>
            )
          })}
        </div>

        <Link href={NINXL_DOWNLOAD_HREF} className="craft-nav__cta">
          Download
        </Link>
      </div>
    </nav>
  )
})

NinCraftNav.displayName = 'NinCraftNav'
